import { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import logoImage from "./assets/kundalini-logo.jpeg";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase =
  supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey)
    : null;

const defaultContent = {
  brand: {
    name: "Kundalini",
    subtitle: "Espaço Tântrico",
    tagline: "Energia • Presença • Conexão",
  },
  contact: {
    whatsapp: "5551999999999",
    whatsappMessage: "Olá! Gostaria de saber mais sobre as terapias.",
  },
  home: {
    title: "Um espaço de serenidade, beleza e reconexão interior.",
    description:
      "O Kundalini acolhe experiências sensoriais profundas em um ambiente elegante, reservado e harmonioso, inspirado pela sutileza da energia e pela arte do cuidado.",
    heroImage: logoImage,
  },
  about: {
    title: "Um refúgio para desacelerar",
    description:
      "O Kundalini foi concebido como um espaço de acolhimento, sutileza e presença. Cada detalhe do ambiente convida ao relaxamento, à sensibilidade do toque e à contemplação do próprio corpo como portal de consciência, bem-estar e energia vital.",
    extraText:
      "Aqui, a experiência é pensada para ser íntima, elegante e serena, unindo estética espiritual, conforto e uma atmosfera de discrição. O espaço valoriza o tempo presente, o cuidado atencioso e a reconexão com a própria energia.",
    images: [],
  },
  therapists: [
    {
      name: "Sofia",
      bio: "Conexão energética profunda, presença sensorial e acolhimento intuitivo.",
      flagText: "Nova terapeuta",
      flagColor: "#6a2d61",
      images: [],
    },
    {
      name: "Luna",
      bio: "Especialista em relaxamento, fluidez corporal e equilíbrio interior.",
      flagText: "",
      flagColor: "#8f755d",
      images: [],
    },
    {
      name: "Aurora",
      bio: "Terapias sensoriais com foco em energia vital, serenidade e reconexão.",
      flagText: "",
      flagColor: "#8f755d",
      images: [],
    },
  ],
  services: [
    {
      title: "Massagem Tântrica",
      subtitle: "Expansão sensorial e presença corporal",
      text:
        "Uma experiência voltada à ampliação da percepção do corpo, ao despertar da sensibilidade e à reconexão com a própria energia.",
      images: [],
      details: [
        "Foco em presença e sensibilidade",
        "Ambiente reservado e acolhedor",
        "Ritmo suave e respeitoso",
      ],
    },
    {
      title: "Terapia Sensorial",
      subtitle: "Toque consciente e relaxamento profundo",
      text:
        "Uma terapia que convida ao desacelerar por meio de estímulos sutis, movimentos suaves e atenção plena ao sentir.",
      images: [],
      details: [
        "Estímulos sensoriais sutis",
        "Atenção plena ao corpo",
        "Auxilia no relaxamento mental",
      ],
    },
  ],
};

function mergeContent(saved) {
  return {
    ...defaultContent,
    ...saved,
    brand: { ...defaultContent.brand, ...(saved?.brand || {}) },
    contact: { ...defaultContent.contact, ...(saved?.contact || {}) },
    home: { ...defaultContent.home, ...(saved?.home || {}) },
    about: { ...defaultContent.about, ...(saved?.about || {}) },
    therapists: saved?.therapists || defaultContent.therapists,
    services: saved?.services || defaultContent.services,
  };
}

function buildWhatsappLink(phone, message) {
  if (!phone) return "#";
  const sanitized = String(phone).replace(/\D/g, "");
  const text = encodeURIComponent(
    message || "Olá! Gostaria de mais informações."
  );
  return `https://wa.me/${sanitized}?text=${text}`;
}

function NavButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`rounded-full border px-4 py-2 text-xs tracking-[0.22em] transition-all duration-300 ${active
        ? "border-[#6a2d61] bg-[#6a2d61] text-[#f6ecdc] shadow-md"
        : "border-[#7f6b58]/40 bg-white/30 text-[#5d274f] hover:bg-white/60"
        }`}
    >
      {label}
    </button>
  );
}

function SectionTitle({ overline, title, text }) {
  return (
    <div className="mb-10 text-center">
      <p className="mb-4 text-xs md:text-sm uppercase tracking-[0.45em] text-[#8f755d]">
        {overline}
      </p>
      <h2 className="font-serif text-4xl leading-tight text-[#5a2149] md:text-5xl lg:text-6xl">
        {title}
      </h2>
      {text ? (
        <p className="mx-auto mt-5 max-w-3xl text-lg md:text-xl leading-8 md:leading-9 text-[#6e5a4b]">
          {text}
        </p>
      ) : null}
    </div>
  );
}

function ImageCarousel({
  images,
  title,
  description,
  imageHeight = "h-[340px] md:h-[420px]",
  flagText = "",
  flagColor = "#6a2d61",
}) {
  const safeImages =
    images?.length > 0
      ? images
      : ["https://via.placeholder.com/900x1200?text=Imagem"];

  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [images]);

  return (
    <div className="overflow-hidden rounded-[28px] border border-[#b9a489]/30 bg-white/40 shadow-[0_14px_40px_rgba(92,52,79,0.12)] backdrop-blur-sm">
      <div className="relative">
        <img
          src={safeImages[index]}
          alt={title}
          className={`${imageHeight} w-full object-cover`}
        />
        {flagText ? (
          <span
            className="absolute left-4 top-4 rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-white shadow-md"
            style={{ backgroundColor: flagColor || "#6a2d61" }}
          >
            {flagText}
          </span>
        ) : null}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent p-6">
          <h3 className="font-serif text-2xl md:text-3xl tracking-[0.08em] text-white">
            {title}
          </h3>
        </div>
      </div>

      <div className="p-6">
        {description ? (
          <p className="text-[15px] leading-7 text-[#5c4a3c]">{description}</p>
        ) : null}

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            onClick={() =>
              setIndex((prev) => (prev - 1 + safeImages.length) % safeImages.length)
            }
            type="button"
            className="rounded-full border border-[#7f6b58]/30 bg-[#f8f0e1] px-3 md:px-4 py-2 text-xs md:text-sm text-[#5d274f] transition hover:bg-[#efe2cb]"
          >
            ← Anterior
          </button>

          <span className="text-xs md:text-sm tracking-[0.2em] text-[#7f6b58]">
            {String(index + 1).padStart(2, "0")} /{" "}
            {String(safeImages.length).padStart(2, "0")}
          </span>

          <button
            onClick={() => setIndex((prev) => (prev + 1) % safeImages.length)}
            type="button"
            className="rounded-full border border-[#7f6b58]/30 bg-[#f8f0e1] px-3 md:px-4 py-2 text-xs md:text-sm text-[#5d274f] transition hover:bg-[#efe2cb]"
          >
            Próxima →
          </button>
        </div>
      </div>
    </div>
  );
}

function TherapistsCarousel({ therapists }) {
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 3;
  const pages = [];

  for (let i = 0; i < therapists.length; i += pageSize) {
    pages.push(therapists.slice(i, i + pageSize));
  }

  const safePages = pages.length ? pages : [[]];
  const currentPage = safePages[pageIndex] || [];

  useEffect(() => {
    if (pageIndex > safePages.length - 1) {
      setPageIndex(0);
    }
  }, [pageIndex, safePages.length]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {currentPage.map((therapist, index) => (
          <ImageCarousel
            key={`${therapist.name}-${index}-${pageIndex}`}
            images={therapist.images}
            title={therapist.name}
            description={therapist.bio}
            imageHeight="h-[420px]"
            flagText={therapist.flagText}
            flagColor={therapist.flagColor}
          />
        ))}
      </div>

      {safePages.length > 1 ? (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() =>
              setPageIndex((prev) => (prev - 1 + safePages.length) % safePages.length)
            }
            type="button"
            className="rounded-full border border-[#7f6b58]/30 bg-[#f8f0e1] px-4 md:px-5 py-2 text-xs md:text-sm text-[#5d274f] transition hover:bg-[#efe2cb]"
          >
            ← Grupo anterior
          </button>
          <span className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#8f755d]">
            {String(pageIndex + 1).padStart(2, "0")} /{" "}
            {String(safePages.length).padStart(2, "0")}
          </span>
          <button
            onClick={() => setPageIndex((prev) => (prev + 1) % safePages.length)}
            type="button"
            className="rounded-full border border-[#7f6b58]/30 bg-[#f8f0e1] px-4 md:px-5 py-2 text-xs md:text-sm text-[#5d274f] transition hover:bg-[#efe2cb]"
          >
            Próximo grupo →
          </button>
        </div>
      ) : null}
    </div>
  );
}

function WhatsappCTA({ contact }) {
  const link = buildWhatsappLink(contact?.whatsapp, contact?.whatsappMessage);

  return (
    <section className="mt-16 rounded-[32px] border border-[#b9a489]/30 bg-white/45 p-8 shadow-[0_14px_40px_rgba(92,52,79,0.10)] backdrop-blur-sm">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs md:text-sm uppercase tracking-[0.4em] text-[#8f755d]">
            Contato
          </p>
          <h3 className="mt-3 font-serif text-3xl md:text-4xl text-[#5a2149] leading-tight">
            Fale conosco pelo WhatsApp
          </h3>
          <p className="mt-3 md:mt-4 max-w-2xl text-[16px] md:text-lg leading-7 md:leading-8 text-[#6e5a4b]">
            Tire dúvidas, conheça melhor as terapias e faça seu contato de forma
            rápida e reservada.
          </p>
        </div>

        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-full md:w-auto items-center justify-center rounded-full bg-[#5a2149] px-7 py-4 text-sm uppercase tracking-[0.22em] text-[#f6ecdc] transition hover:bg-[#6b2f58] text-center"
        >
          Chamar no WhatsApp
        </a>
      </div>
    </section>
  );
}

function PublicSite({ content, page, setPage }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleNav = (target) => {
    setPage(target);
    setMenuOpen(false);
  };

  return (
    <>
      <header className="fixed left-0 right-0 top-0 z-40 border-b border-[#b9a489]/35 bg-[#efe4cf]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4">
          <div className="flex items-center gap-4 relative z-50">
            <img
              src={logoImage}
              alt="Kundalini Espaço Tântrico"
              className="h-14 w-14 rounded-full object-cover shadow-md"
            />
            <div>
              <h1 className="font-serif text-2xl md:text-3xl uppercase tracking-[0.18em] text-[#5a2149]">
                {content.brand.name}
              </h1>
              <p className="text-[9px] md:text-[11px] uppercase tracking-[0.3em] md:tracking-[0.42em] text-[#7f6b58]">
                {content.brand.subtitle}
              </p>
            </div>
          </div>

          <nav className="hidden flex-wrap items-center gap-3 md:flex">
            <NavButton label="INÍCIO" active={page === "home"} onClick={() => setPage("home")} />
            <NavButton label="A CASA" active={page === "about"} onClick={() => setPage("about")} />
            <NavButton
              label="TERAPEUTAS"
              active={page === "therapists"}
              onClick={() => setPage("therapists")}
            />
            <NavButton
              label="TERAPIAS"
              active={page === "services"}
              onClick={() => setPage("services")}
            />
          </nav>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="relative z-50 p-2 text-[#5a2149] md:hidden"
            aria-label="Abrir menu"
          >
            {menuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {menuOpen && (
          <div className="fixed inset-0 top-[88px] z-40 flex justify-center bg-[#efe4cf]/95 px-6 py-8 backdrop-blur-md md:hidden h-[calc(100vh-88px)] overflow-y-auto">
            <div className="flex flex-col gap-6 items-center pt-8">
              <button
                onClick={() => handleNav("home")}
                className={`text-2xl font-serif tracking-[0.1em] ${page === "home" ? "text-[#5a2149] font-bold" : "text-[#7f6b58]"}`}
              >
                INÍCIO
              </button>
              <button
                onClick={() => handleNav("about")}
                className={`text-2xl font-serif tracking-[0.1em] ${page === "about" ? "text-[#5a2149] font-bold" : "text-[#7f6b58]"}`}
              >
                A CASA
              </button>
              <button
                onClick={() => handleNav("therapists")}
                className={`text-2xl font-serif tracking-[0.1em] ${page === "therapists" ? "text-[#5a2149] font-bold" : "text-[#7f6b58]"}`}
              >
                TERAPEUTAS
              </button>
              <button
                onClick={() => handleNav("services")}
                className={`text-2xl font-serif tracking-[0.1em] ${page === "services" ? "text-[#5a2149] font-bold" : "text-[#7f6b58]"}`}
              >
                TERAPIAS
              </button>

              <div className="w-12 h-px bg-[#b9a489]/50 my-6" />

              <a
                href={buildWhatsappLink(content.contact?.whatsapp, content.contact?.whatsappMessage)}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-[#5a2149] px-8 py-4 text-sm uppercase tracking-[0.2em] text-[#f6ecdc]"
              >
                Falar conosco
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-6 pb-28 pt-28 md:pt-24">
        {page === "home" && (
          <section className="grid min-h-[calc(100vh-220px)] items-center gap-12 md:grid-cols-2">
            <div className="flex flex-col">
              <p className="mb-6 md:mb-4 text-xs md:text-sm uppercase tracking-[0.45em] text-[#8f755d] order-1">
                {content.brand.tagline}
              </p>

              <div className="order-2 mb-8 flex justify-center md:hidden">
                <div className="overflow-hidden rounded-[34px] border border-[#b9a489]/40 bg-white/30 p-2 sm:p-4 shadow-[0_20px_60px_rgba(90,33,73,0.18)] backdrop-blur-sm w-full max-w-[400px]">
                  <img
                    src={content.home.heroImage || logoImage}
                    alt="Imagem principal Kundalini"
                    className="h-auto w-full flex-shrink-0 rounded-[26px] object-contain max-h-[320px]"
                  />
                </div>
              </div>

              <h2 className="max-w-xl font-serif text-4xl leading-[1.1] text-[#5a2149] md:text-6xl lg:text-7xl order-3">
                {content.home.title}
              </h2>
              <p className="mt-5 md:mt-6 max-w-xl text-lg md:text-xl leading-8 md:leading-9 text-[#6e5a4b] order-4">
                {content.home.description}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap order-5">
                <button
                  onClick={() => setPage("services")}
                  type="button"
                  className="w-full sm:w-auto rounded-full bg-[#5a2149] px-7 py-4 text-xs sm:text-sm uppercase tracking-[0.25em] text-[#f6ecdc] transition hover:bg-[#6b2f58] text-center"
                >
                  Conheça as terapias
                </button>
                <button
                  onClick={() => setPage("about")}
                  type="button"
                  className="w-full sm:w-auto rounded-full border border-[#9f8a74] px-7 py-4 text-xs sm:text-sm uppercase tracking-[0.25em] text-[#5a2149] transition hover:bg-white/50 text-center"
                >
                  Sobre a casa
                </button>
                <a
                  href={buildWhatsappLink(
                    content.contact?.whatsapp,
                    content.contact?.whatsappMessage
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full sm:w-auto rounded-full border border-[#6a2d61] px-7 py-4 text-xs sm:text-sm uppercase tracking-[0.25em] text-[#5a2149] transition hover:bg-white/50 block text-center"
                >
                  WhatsApp
                </a>
              </div>
            </div>

            <div className="hidden md:flex justify-center md:justify-end">
              <div className="overflow-hidden rounded-[34px] border border-[#b9a489]/40 bg-white/30 p-4 shadow-[0_20px_60px_rgba(90,33,73,0.18)] backdrop-blur-sm">
                <img
                  src={content.home.heroImage || logoImage}
                  alt="Imagem principal Kundalini"
                  className="h-auto w-full max-w-[520px] rounded-[26px] object-cover"
                />
              </div>
            </div>
          </section>
        )}

        {page === "about" && (
          <section className="grid w-full max-w-6xl items-center gap-10 lg:grid-cols-2">
            <div className="flex flex-col">
              <p className="mb-6 md:mb-4 text-xs md:text-sm uppercase tracking-[0.45em] text-[#8f755d] order-1">
                A Casa
              </p>

              <div className="order-2 mb-8 lg:hidden">
                <ImageCarousel
                  images={content.about.images}
                  title="Ambientes da Casa"
                  description=""
                  imageHeight="h-[340px] md:h-[430px]"
                />
              </div>

              <h2 className="font-serif text-4xl leading-tight text-[#5a2149] md:text-5xl lg:text-6xl order-3">
                {content.about.title}
              </h2>
              <p className="mt-6 md:mt-8 text-lg md:text-xl leading-8 md:leading-9 text-[#6e5a4b] order-4">
                {content.about.description}
              </p>
              <div className="mt-8 rounded-[30px] border border-[#b9a489]/35 bg-white/35 p-8 text-left shadow-[0_14px_40px_rgba(92,52,79,0.10)] order-5">
                <p className="text-lg leading-8 text-[#5c4a3c]">
                  {content.about.extraText}
                </p>
              </div>
            </div>

            <div className="hidden lg:block">
              <ImageCarousel
                images={content.about.images}
                title="Ambientes da Casa"
                description="Um espaço reservado, harmônico e preparado para acolher com conforto, beleza e serenidade."
                imageHeight="h-[340px] md:h-[430px]"
              />
            </div>
          </section>
        )}

        {page === "therapists" && (
          <section className="w-full">
            <SectionTitle overline="Terapeutas" title="Presença, técnica e sensibilidade" />
            <TherapistsCarousel therapists={content.therapists || []} />
          </section>
        )}

        {page === "services" && (
          <section className="w-full max-w-6xl">
            <SectionTitle
              overline="Terapias"
              title="Rituais de cuidado e expansão"
              text="Cada experiência é pensada para oferecer acolhimento, presença e uma vivência profunda de bem-estar."
            />

            <div className="grid gap-6 md:grid-cols-2">
              {(content.services || []).map((service, index) => {
                const hasImage = service.images?.length > 0;
                return (
                  <div
                    key={index}
                    className="overflow-hidden rounded-[28px] border border-[#b9a489]/30 bg-white/35 shadow-[0_14px_40px_rgba(92,52,79,0.10)]"
                  >
                    {hasImage ? (
                      <div className="relative">
                        <img
                          src={service.images[0]}
                          alt={service.title}
                          className="h-[360px] md:h-[420px] w-full object-cover"
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-6 pb-6 pt-16">
                          <p className="mb-1 text-xs uppercase tracking-[0.35em] text-white/70">
                            {String(index + 1).padStart(2, "0")}
                          </p>
                          <h3 className="font-serif text-2xl md:text-3xl tracking-[0.06em] text-white">
                            {service.title}
                          </h3>
                        </div>
                      </div>
                    ) : (
                      <div className="px-8 pt-8">
                        <p className="mb-3 text-xs md:text-sm uppercase tracking-[0.35em] text-[#8f755d]">
                          {String(index + 1).padStart(2, "0")}
                        </p>
                        <h3 className="font-serif text-2xl md:text-3xl text-[#5a2149]">
                          {service.title}
                        </h3>
                      </div>
                    )}

                    <div className="p-8 pt-6">
                      {service.subtitle ? (
                        <p className="mb-4 text-xs md:text-sm uppercase tracking-[0.28em] text-[#8f755d]">
                          {service.subtitle}
                        </p>
                      ) : null}
                      <p className="text-[16px] md:text-lg leading-7 md:leading-8 text-[#5c4a3c]">
                        {service.text}
                      </p>
                      <ul className="mt-6 space-y-3">
                        {(service.details || []).map((detail, detailIndex) => (
                          <li
                            key={detailIndex}
                            className="flex items-start gap-3 text-[16px] leading-7 text-[#6a5748]"
                          >
                            <span className="mt-1 text-[#5a2149]">✦</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <WhatsappCTA contact={content.contact} />
      </main>

      <a
        href={buildWhatsappLink(content.contact?.whatsapp, content.contact?.whatsappMessage)}
        target="_blank"
        rel="noreferrer"
        title="Falar no WhatsApp"
        className="fixed bottom-[88px] right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_4px_14px_rgba(37,211,102,0.4)] transition hover:scale-105 md:hidden"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16">
          <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
        </svg>
      </a>

      <footer className="static md:fixed bottom-0 left-0 right-0 z-40 border-t border-[#b9a489]/35 bg-[#efe4cf]/92 md:backdrop-blur-md w-full">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-6 py-4 text-center md:flex-row md:text-left">
          <p className="text-sm uppercase tracking-[0.28em] text-[#6d5660]">
            © 2026 {content.brand.name} {content.brand.subtitle}
          </p>
          <div className="flex flex-col items-center gap-2 md:items-end">
            <p className="text-xs uppercase tracking-[0.35em] text-[#8f755d]">
              Serenidade • Energia • Presença
            </p>
            <a
              href={buildWhatsappLink(
                content.contact?.whatsapp,
                content.contact?.whatsappMessage
              )}
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[0.28em] text-[#5a2149] underline-offset-4 hover:underline"
            >
              Entrar em contato
            </a>
          </div>
        </div>
      </footer>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea = false,
  placeholder = "",
  type = "text",
}) {
  const common =
    "mt-2 w-full rounded-2xl border border-[#ccbba0] bg-white/80 px-4 py-3 text-[#5c4a3c] outline-none transition focus:border-[#6a2d61]";

  return (
    <label className="block">
      <span className="text-sm uppercase tracking-[0.22em] text-[#8f755d]">
        {label}
      </span>
      {textarea ? (
        <textarea
          className={`${common} min-h-[120px]`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <input
          className={common}
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </label>
  );
}

function UploadField({ label, multiple = false, onUpload, folder = "general" }) {
  return (
    <label className="inline-flex cursor-pointer items-center rounded-full border border-[#7f6b58]/30 bg-[#f8f0e1] px-4 py-2 text-sm text-[#5d274f] transition hover:bg-[#efe2cb]">
      {label}
      <input
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={async (e) => {
          if (!supabase) {
            alert("Supabase não configurado.");
            return;
          }

          const files = Array.from(e.target.files || []);
          if (!files.length) return;

          const urls = [];

          for (const file of files) {
            const fileExt = file.name.split(".").pop();
            const fileName = `${folder}/${Date.now()}-${Math.random()
              .toString(36)
              .slice(2)}.${fileExt}`;

            const { error } = await supabase.storage
              .from("kundalini-images")
              .upload(fileName, file, { upsert: true });

            if (error) {
              alert(`Erro no upload: ${error.message}`);
              return;
            }

            const { data } = supabase.storage
              .from("kundalini-images")
              .getPublicUrl(fileName);

            urls.push(data.publicUrl);
          }

          onUpload(multiple ? urls : urls[0]);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function AdminCard({ title, children }) {
  return (
    <section className="rounded-[28px] border border-[#b9a489]/30 bg-white/45 p-6 shadow-[0_14px_40px_rgba(92,52,79,0.10)] backdrop-blur-sm">
      <h3 className="mb-5 font-serif text-3xl text-[#5a2149]">{title}</h3>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function ImageListEditor({ images, setImages, folder }) {
  const list = images || [];

  const updateImage = (index, value) => {
    const next = [...list];
    next[index] = value;
    setImages(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <UploadField
          label="Enviar imagens"
          multiple
          folder={folder}
          onUpload={(files) => setImages([...(list || []), ...files])}
        />
        <button
          onClick={() => setImages([...(list || []), ""])}
          type="button"
          className="rounded-full border border-[#7f6b58]/30 bg-white/70 px-4 py-2 text-sm text-[#5d274f]"
        >
          Adicionar campo
        </button>
      </div>

      <div className="space-y-4">
        {list.map((image, index) => (
          <div key={index} className="rounded-2xl border border-[#ddcfbb] bg-white/70 p-4">
            <div className="grid gap-4 md:grid-cols-[120px_1fr_auto] md:items-center">
              <img
                src={image || "https://via.placeholder.com/120?text=Imagem"}
                alt={`Imagem ${index + 1}`}
                className="h-24 w-24 rounded-xl object-cover"
              />
              <input
                value={image || ""}
                onChange={(e) => updateImage(index, e.target.value)}
                className="w-full rounded-xl border border-[#ccbba0] bg-white px-4 py-3 text-[#5c4a3c] outline-none"
              />
              <button
                onClick={() => setImages(list.filter((_, i) => i !== index))}
                type="button"
                className="rounded-full border border-red-300 px-4 py-2 text-sm text-red-600"
              >
                Remover
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailsEditor({ details, setDetails }) {
  const list = details || [];

  const updateDetail = (index, value) => {
    const next = [...list];
    next[index] = value;
    setDetails(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setDetails([...(list || []), "Novo destaque"])}
          type="button"
          className="rounded-full border border-[#7f6b58]/30 bg-white/70 px-4 py-2 text-sm text-[#5d274f]"
        >
          Adicionar destaque
        </button>
      </div>

      <div className="space-y-3">
        {list.map((detail, index) => (
          <div key={index} className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <Field
              label={`Destaque ${index + 1}`}
              value={detail}
              onChange={(value) => updateDetail(index, value)}
            />
            <button
              onClick={() => setDetails(list.filter((_, i) => i !== index))}
              type="button"
              className="rounded-full border border-red-300 px-4 py-2 text-sm text-red-600"
            >
              Remover
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminPanel({ content, setContent, onClose, onLogout }) {
  const updateBrand = (field, value) =>
    setContent((prev) => ({
      ...prev,
      brand: { ...prev.brand, [field]: value },
    }));

  const updateContact = (field, value) =>
    setContent((prev) => ({
      ...prev,
      contact: { ...prev.contact, [field]: value },
    }));

  const updateHome = (field, value) =>
    setContent((prev) => ({
      ...prev,
      home: { ...prev.home, [field]: value },
    }));

  const updateAbout = (field, value) =>
    setContent((prev) => ({
      ...prev,
      about: { ...prev.about, [field]: value },
    }));

  const updateTherapist = (index, field, value) =>
    setContent((prev) => ({
      ...prev,
      therapists: prev.therapists.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));

  const updateService = (index, field, value) =>
    setContent((prev) => ({
      ...prev,
      services: prev.services.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));

  const addTherapist = () =>
    setContent((prev) => ({
      ...prev,
      therapists: [
        ...prev.therapists,
        {
          name: "Nova terapeuta",
          bio: "Descrição da terapeuta.",
          flagText: "",
          flagColor: "#6a2d61",
          images: [],
        },
      ],
    }));

  const removeTherapist = (index) =>
    setContent((prev) => ({
      ...prev,
      therapists: prev.therapists.filter((_, i) => i !== index),
    }));

  const addService = () =>
    setContent((prev) => ({
      ...prev,
      services: [
        ...prev.services,
        {
          title: "Nova terapia",
          subtitle: "Subtítulo da terapia",
          text: "Descrição completa da terapia.",
          images: [],
          details: ["Destaque 1"],
        },
      ],
    }));

  const removeService = (index) =>
    setContent((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));

  const saveAll = async () => {
    if (!supabase) {
      alert("Configure REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY.");
      return;
    }

    const payload = {
      id: 1,
      content_json: content,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase.from("site_content").upsert(payload);

    if (error) {
      alert(`Erro ao salvar: ${error.message}`);
      return;
    }

    alert("Alterações salvas no banco.");
  };

  return (
    <div className="min-h-screen bg-[#efe4cf] px-4 pb-10 pt-8 text-[#512447] md:px-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-[#8f755d]">
              Painel administrativo
            </p>
            <h2 className="mt-2 font-serif text-5xl text-[#5a2149]">
              Conteúdo conectado ao banco
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={saveAll}
              type="button"
              className="rounded-full bg-[#5a2149] px-6 py-3 text-sm uppercase tracking-[0.22em] text-[#f6ecdc]"
            >
              Salvar no banco
            </button>
            <button
              onClick={onClose}
              type="button"
              className="rounded-full border border-[#7f6b58]/30 bg-white/70 px-6 py-3 text-sm uppercase tracking-[0.22em] text-[#5d274f]"
            >
              Voltar ao site
            </button>
            <button
              onClick={onLogout}
              type="button"
              className="rounded-full border border-[#7f6b58]/30 bg-white/70 px-6 py-3 text-sm uppercase tracking-[0.22em] text-[#5d274f]"
            >
              Sair
            </button>
          </div>
        </div>

        <div className="grid gap-6">
          <AdminCard title="Marca e cabeçalho">
            <div className="grid gap-5 md:grid-cols-3">
              <Field label="Nome" value={content.brand.name} onChange={(v) => updateBrand("name", v)} />
              <Field
                label="Subtítulo"
                value={content.brand.subtitle}
                onChange={(v) => updateBrand("subtitle", v)}
              />
              <Field
                label="Tagline"
                value={content.brand.tagline}
                onChange={(v) => updateBrand("tagline", v)}
              />
            </div>
          </AdminCard>

          <AdminCard title="Contato por WhatsApp">
            <div className="grid gap-5 md:grid-cols-2">
              <Field
                label="Número do WhatsApp"
                value={content.contact?.whatsapp}
                onChange={(v) => updateContact("whatsapp", v)}
                placeholder="5551999999999"
              />
              <Field
                label="Mensagem inicial"
                value={content.contact?.whatsappMessage}
                onChange={(v) => updateContact("whatsappMessage", v)}
                placeholder="Olá! Gostaria de saber mais sobre as terapias."
              />
            </div>
          </AdminCard>

          <AdminCard title="Página inicial">
            <Field
              label="Título principal"
              value={content.home.title}
              onChange={(v) => updateHome("title", v)}
              textarea
            />
            <Field
              label="Descrição"
              value={content.home.description}
              onChange={(v) => updateHome("description", v)}
              textarea
            />
            <Field
              label="Imagem principal (URL)"
              value={content.home.heroImage}
              onChange={(v) => updateHome("heroImage", v)}
            />
            <UploadField
              label="Enviar imagem principal"
              folder="home"
              onUpload={(file) => updateHome("heroImage", file)}
            />
          </AdminCard>

          <AdminCard title="Página A Casa">
            <Field label="Título" value={content.about.title} onChange={(v) => updateAbout("title", v)} />
            <Field
              label="Descrição principal"
              value={content.about.description}
              onChange={(v) => updateAbout("description", v)}
              textarea
            />
            <Field
              label="Texto complementar"
              value={content.about.extraText}
              onChange={(v) => updateAbout("extraText", v)}
              textarea
            />
            <ImageListEditor
              images={content.about.images}
              setImages={(images) => updateAbout("images", images)}
              folder="about"
            />
          </AdminCard>

          <AdminCard title="Terapeutas">
            <div className="mb-4 flex justify-end">
              <button
                onClick={addTherapist}
                type="button"
                className="rounded-full bg-[#5a2149] px-5 py-2 text-sm uppercase tracking-[0.18em] text-[#f6ecdc]"
              >
                Adicionar terapeuta
              </button>
            </div>

            <div className="space-y-6">
              {(content.therapists || []).map((therapist, index) => (
                <div key={index} className="rounded-[24px] border border-[#ddcfbb] bg-white/65 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="font-serif text-2xl text-[#5a2149]">
                      Terapeuta {index + 1}
                    </h4>
                    <button
                      onClick={() => removeTherapist(index)}
                      type="button"
                      className="rounded-full border border-red-300 px-4 py-2 text-sm text-red-600"
                    >
                      Remover
                    </button>
                  </div>

                  <div className="grid gap-5">
                    <Field
                      label="Nome"
                      value={therapist.name}
                      onChange={(v) => updateTherapist(index, "name", v)}
                    />
                    <Field
                      label="Biografia"
                      value={therapist.bio}
                      onChange={(v) => updateTherapist(index, "bio", v)}
                      textarea
                    />
                    <div className="grid gap-5 md:grid-cols-2">
                      <Field
                        label="Texto da flag"
                        value={therapist.flagText}
                        onChange={(v) => updateTherapist(index, "flagText", v)}
                        placeholder="Nova terapeuta"
                      />
                      <Field
                        label="Cor da flag"
                        value={therapist.flagColor}
                        onChange={(v) => updateTherapist(index, "flagColor", v)}
                        type="color"
                      />
                    </div>
                    <ImageListEditor
                      images={therapist.images}
                      setImages={(images) => updateTherapist(index, "images", images)}
                      folder={`therapists-${index + 1}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </AdminCard>

          <AdminCard title="Terapias">
            <div className="mb-4 flex justify-end">
              <button
                onClick={addService}
                type="button"
                className="rounded-full bg-[#5a2149] px-5 py-2 text-sm uppercase tracking-[0.18em] text-[#f6ecdc]"
              >
                Adicionar terapia
              </button>
            </div>

            <div className="space-y-6">
              {(content.services || []).map((service, index) => (
                <div key={index} className="rounded-[24px] border border-[#ddcfbb] bg-white/65 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="font-serif text-2xl text-[#5a2149]">
                      Terapia {index + 1}
                    </h4>
                    <button
                      onClick={() => removeService(index)}
                      type="button"
                      className="rounded-full border border-red-300 px-4 py-2 text-sm text-red-600"
                    >
                      Remover
                    </button>
                  </div>

                  <div className="grid gap-5">
                    <Field
                      label="Título"
                      value={service.title}
                      onChange={(v) => updateService(index, "title", v)}
                    />
                    <Field
                      label="Subtítulo"
                      value={service.subtitle}
                      onChange={(v) => updateService(index, "subtitle", v)}
                    />
                    <Field
                      label="Descrição"
                      value={service.text}
                      onChange={(v) => updateService(index, "text", v)}
                      textarea
                    />
                    <ImageListEditor
                      images={service.images}
                      setImages={(images) => updateService(index, "images", images)}
                      folder={`services-${index + 1}`}
                    />
                    <DetailsEditor
                      details={service.details}
                      setDetails={(details) => updateService(index, "details", details)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </AdminCard>
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();

    if (!supabase) {
      setError("Configure as variáveis do Supabase.");
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError(error.message);
      return;
    }

    setError("");
    onLogin();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#efe4cf] px-4 text-[#512447]">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-[32px] border border-[#b9a489]/30 bg-white/50 p-8 shadow-[0_20px_60px_rgba(90,33,73,0.12)] backdrop-blur-sm"
      >
        <p className="text-sm uppercase tracking-[0.42em] text-[#8f755d]">
          Acesso administrativo
        </p>
        <h2 className="mt-4 font-serif text-5xl text-[#5a2149]">Entrar</h2>
        <p className="mt-4 text-lg leading-8 text-[#6e5a4b]">
          Acesse pelo endereço com #admin para editar o conteúdo.
        </p>

        <div className="mt-6 space-y-5">
          <Field
            label="E-mail"
            value={email}
            onChange={setEmail}
            placeholder="admin@empresa.com"
            type="email"
          />
          <Field
            label="Senha"
            value={password}
            onChange={setPassword}
            placeholder="Sua senha"
            type="password"
          />
        </div>

        {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}

        <button className="mt-6 w-full rounded-full bg-[#5a2149] px-6 py-3 text-sm uppercase tracking-[0.22em] text-[#f6ecdc]">
          Entrar no painel
        </button>
      </form>
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState("home");
  const [content, setContent] = useState(defaultContent);
  const [authenticated, setAuthenticated] = useState(false);
  const [adminMode, setAdminMode] = useState(window.location.hash === "#admin");

  useEffect(() => {
    const handleHashChange = () => {
      setAdminMode(window.location.hash === "#admin");
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  useEffect(() => {
    async function bootstrap() {
      if (!supabase) return;

      const {
        data: { session },
      } = await supabase.auth.getSession();

      setAuthenticated(!!session);

      const { data, error } = await supabase
        .from("site_content")
        .select("content_json")
        .eq("id", 1)
        .maybeSingle();

      if (!error && data?.content_json) {
        setContent(mergeContent(data.content_json));
      }
    }

    bootstrap();
  }, []);

  const handleLogout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    setAuthenticated(false);
  };

  const goToSite = () => {
    window.location.hash = "";
  };

  return (
    <div
      className="min-h-screen bg-[#efe4cf] text-[#512447]"
      style={{
        fontFamily: '"Cormorant Garamond", "Times New Roman", Georgia, serif',
        backgroundImage:
          "radial-gradient(circle at top, rgba(255,255,255,0.35), transparent 30%), linear-gradient(rgba(255,248,235,0.55), rgba(255,248,235,0.55))",
      }}
    >
      {adminMode ? (
        authenticated ? (
          <AdminPanel
            content={content}
            setContent={setContent}
            onClose={goToSite}
            onLogout={handleLogout}
          />
        ) : (
          <LoginScreen onLogin={() => setAuthenticated(true)} />
        )
      ) : (
        <PublicSite content={content} page={page} setPage={setPage} />
      )}
    </div>
  );
}